import React from "react";

const EssenceOfAPersonalBrandVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
        This lesson defines personal brand as what people associate with you when you are not in the room. It argues brand is not your resume or skills, but repeated signals through character, behavior, and presence that create instant recall. It shows that everyone already has a brand, chosen or accidental, and that trust comes from the feeling you leave behind, clarity, fairness, courage, calm. The key is consistency and alignment with the next level.
      </p>
    </div>
  );

const transcript = (
  <div className="pt-2 pb-6 space-y-4 text-body leading-7 text-[#323232] overflow-y-scroll font-gotham">
    <p>
      If I say non-violence, walking stick or white dhoti, I am sure we both
      thought of the same person. Mahatma Gandhi.
    </p>

    <p>
      46664. Open palm. Forgiveness and equality? Nelson Mandela right?
    </p>

    <p>
      Pause the video here and Google Steve Jobs. Can you find one picture in
      which he is not wearing a black T-shirt?
    </p>

    <p>
      The point is none of those are their <em>skills</em>.
    </p>

    <p>
      Gandhi was a brilliant strategist.
      <br />
      Nelson Mandela, a powerful orator.
      <br />
      Steve Jobs was a visionary presenter.
    </p>

    <p>
      But that’s not what you remembered, is it? You remembered what they{" "}
      <em>stood for</em>.
      <br />
      And that is their personal brand.
    </p>

    <p>
      That’s the first truth about branding, your brand isn’t your résumé.
      <br />
      It’s not the bullet points under your name. It’s the{" "}
      <em>feeling</em> people associate with you when you’re not in the room.
    </p>

    <p>
      Your brand is the sum total of your{" "}
      <strong>character, behaviour, and appearance</strong>. Basically, the
      signals you send again and again until people don’t need a reminder,
      they just <em>know</em> that it’s you.
    </p>

    <p>
      In the workplace, every person already has a brand.
      <br />
      The calm in a crisis one. The “let’s circle back” person who does not
      circle back. The one who always fixes things at 11:59pm for no reason and
      then complains about it later? Always late to meetings.
    </p>

    <p>Even silence sends a message.</p>

    <p>
      Similarly, right now, somewhere in your company, people are forming
      opinions about you. Your manager. Your skip-level. The promotion
      committee. Almost anyone, even the security personnel might have an
      opinion about you.
    </p>

    <p>
      That opinion is your brand. The only question is, did you choose it or
      was it chosen for you?
    </p>

    <p>
      Skills make you eligible. But your brand makes you{" "}
      <em>trusted</em>.
    </p>

    <p>
      Because people don’t remember what you did, they remember how you made
      them feel.
    </p>

    <p>
      Did you bring clarity when things got messy?
      <br />
      Did you stay fair when things got political?
      <br />
      Did you show courage when everyone played safe?
    </p>

    <p>
      That’s the brand. It’s <em>behaviour that repeats</em> until it becomes
      an identity. Your unique <em>identity</em>.
      <br />
      And no your ID card does not count!
    </p>

    <p>[Pause and shift the frame]</p>

    <p>
      So ask yourself, or more accurately others, what do people{" "}
      <em>feel</em> after working with you? Do they leave with clarity? Or
      confusion? Trust? Or tension?
      <br />
      Your skill got the job done. But your <em>brand</em> decides if people
      want to work with you again.
    </p>

    <p>
      That’s why high performers don’t just build capability, they build{" "}
      <em>recall</em>.
    </p>

    <p>
      Think about it: Gandhi’s dhoti and non-violence. Mandela’s number and
      equality. Jobs’ black t-shirt and people centric design.
      <br />
      Simple. Consistent. Instantly recognizable.
    </p>

    <p>
      You don’t need a costume or a slogan. But you do need{" "}
      <em>consistency</em>.
      <br />
      Something about how you show up — your values, tone, presence, or
      principle — that creates instant recognition.
    </p>

    <p>
      That’s what turns competence into <em>memory</em>.
    </p>

    <p>
      Your personal brand is not what you <em>can</em> do. It’s what people{" "}
      <em>expect</em> from you.
    </p>

    <p>
      And here’s the catch, what you stand for today must align with where you
      want to go next.
    </p>

    <p>
      Because the next level doesn’t just ask,
      <br />
      “Can you do the job?”
      <br />
      It asks,
      <br />
      “Do you look like you belong there?”
    </p>

    <p>
      What you stand for is what they remember and not what you brought for
      lunch yesterday.
    </p>

    <p>What do people associate with you?</p>
  </div>
);

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  return { about, transcript };
};

export default EssenceOfAPersonalBrandVideo;
